import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Radio, AlertCircle } from 'lucide-react';
import apiClient from '../api/client';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<{ id: string; name: string; role: string }[]>([]);
  const [showUsers, setShowUsers] = useState(false);

  const handleLogin = async (userId?: string) => {
    setError('');
    setLoading(true);
    try {
      await login(pin, userId);
      navigate('/');
    } catch {
      setError('Invalid PIN or login failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pin.trim()) return;

    // Try direct login first
    try {
      setLoading(true);
      await login(pin);
      navigate('/');
    } catch {
      // Multi-user PIN — load user list
      try {
        const demoUsers = await apiClient.getDemoUsers();
        setUsers(demoUsers.map(u => ({ id: u.id, name: u.name, role: u.role })));
        setShowUsers(true);
      } catch {
        setError('Invalid PIN');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-rtcc-dark flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center p-4 bg-rtcc-accent/10 rounded-2xl mb-4">
            <Radio className="h-10 w-10 text-rtcc-accent" />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-wider">RTCC</h1>
          <p className="text-sm text-slate-400 mt-1">Real-Time Crime Center — Demo v1</p>
        </div>

        {!showUsers ? (
          <form onSubmit={handlePinSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">
                Access PIN
              </label>
              <input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                className="w-full px-4 py-3 bg-rtcc-panel border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-rtcc-accent focus:border-transparent"
                placeholder="Enter PIN"
                autoFocus
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !pin.trim()}
              className="w-full py-3 bg-rtcc-accent text-white font-medium rounded-lg hover:bg-rtcc-accent/90 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Connecting...' : 'Enter Command Center'}
            </button>
          </form>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-400 text-center">Select operator:</p>
            {users.map((u) => (
              <button
                key={u.id}
                onClick={() => handleLogin(u.id)}
                disabled={loading}
                className="w-full px-4 py-3 bg-rtcc-panel border border-slate-600 rounded-lg text-left hover:border-rtcc-accent transition-colors"
              >
                <span className="text-white font-medium">{u.name}</span>
                <span className="block text-xs text-slate-400">{u.role}</span>
              </button>
            ))}
            <button
              onClick={() => { setShowUsers(false); setPin(''); }}
              className="w-full py-2 text-sm text-slate-400 hover:text-white transition-colors"
            >
              Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
